import React, { useState, useEffect, useContext } from "react";
import Task from "./Task";
import NewToDo from "./NewToDo";
import EditToDo from "./EditToDo";
import { v4 as uuidv4 } from "uuid";
import { Typography, Grid, Fade, useMediaQuery, useTheme } from "@mui/material";
import { where, or, getDocs, query, collection, doc, updateDoc, onSnapshot, addDoc } from "firebase/firestore";
import Board from "@lourenci/react-kanban";
import "@lourenci/react-kanban/dist/styles.css";
import "./board.css";
import { listenAndFilterTo, useFetchCollectionDocs } from "apis/dataSnapshots";
import { Context } from "context/Wrapper";
import { db } from "config/config";
import { useMemo } from "react";
import {
  DndContext,
  closestCorners,
  useSensor,
  useSensors,
  PointerSensor,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import { TouchSensor } from "@dnd-kit/core";

const ToDo = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeCardId, setActiveCardId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 10,
      },
    }),
  );

  const context = useContext(Context);
  const { groupId, isPro, userId } = context?.state?.userProfile || {};
  const user = context?.state?.userProfile || {};
  const { showAllDataFlag } = context?.state || {};

  const contacts = useFetchCollectionDocs({
    collectionName: "contacts",
    conditions:
      userId && groupId && isPro && showAllDataFlag
        ? [or(where("groupId", "==", groupId), where("userId", "==", userId))]
        : userId
          ? [where("userId", "==", userId)]
          : [],
    setContext: context.setState,
  });

  const groupUsers = useFetchCollectionDocs({
    collectionName: "profiles",
    conditions: groupId ? [where("groupId", "==", groupId)] : [],
    setContext: context.setState,
  });

  const [isUpdating, setUpdating] = useState(true);
  const [editOpen, setEditOpen] = useState({
    open: false,
    values: { title: "" },
  });

  const [todos, setTodos] = useState();
  const getMyDoc = () => todos?.find(d => d.userId === userId);

  const [projects, setProjects] = useState([]);
  const defaultBoard = {
    columns: [
      { id: 1, title: "Backlog", cards: [] },
      { id: 2, title: "In Progress", cards: [] },
      { id: 3, title: "For Review", cards: [] },
      { id: 4, title: "Completed", cards: [] },
      { id: 5, title: "Archived", cards: [] },
    ],
  };

  const isJobTask = (taskId) => {
    return projects.some(project => project.id === taskId);
  };

  useEffect(() => {
    if (!groupId) return;
    const projectsRef = collection(db, "projects");
    const q = query(projectsRef, where("groupId", "==", groupId));
    const unsubscribe = onSnapshot(q, snap => {
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [groupId]);


  useEffect(() => {
    if (!groupId) return;
    const todosRef = collection(db, "todos");
    const q = showAllDataFlag
      ? query(todosRef, where("groupId", "==", groupId))
      : query(
        todosRef,
        where("groupId", "==", groupId),
        where("userId", "==", userId)
      );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setTodos(data);
      setUpdating(false);

    });

    return () => unsubscribe();
  }, [groupId, userId, showAllDataFlag]);


const board = useMemo(() => {
  if (!todos) return defaultBoard;

  const validJobIds = new Set(projects.map((p) => p.id));

  const myDoc = todos.find(d => d.userId === userId);
  const base = myDoc?.board || defaultBoard;
  const isSampleDeleted = myDoc?.sampleDeleted || false;

  const merged = {
    ...base,
    columns: base.columns.map((col) => ({
      ...col,
      cards: col.cards
        .filter((card) =>
          card.id === "sample-card"
            ? true
            : !card.isJobTask || validJobIds.has(card.id)
        )
    })),
  };

  projects.forEach(job => {
    if (!job.assignedTo) return;

    // Remove the job from all columns first
    merged.columns = merged.columns.map(col => ({
      ...col,
      cards: col.cards.filter(c => c.id !== job.id)
    }));

    const ownerDoc = todos.find(d => d.userId === job.assignedTo);
    const ownerBoard = ownerDoc?.board || defaultBoard;

    // Improved logic for determining column placement
    let preferredColId;
    
    if (job.kanbanColumnId && merged.columns.find(col => col.id === job.kanbanColumnId)) {
      // Use the explicitly set kanban column if it exists and is valid
      preferredColId = job.kanbanColumnId;
    } else {
      // Find existing position in owner's board, or default to backlog
      const existingCol = ownerBoard.columns.find((col) => col.cards.some((c) => c.id === job.id));
      preferredColId = existingCol?.id ?? merged.columns[0].id;
    }

    const targetCol = merged.columns.find((c) => c.id === preferredColId) || merged.columns[0];

    targetCol.cards.push({
      id: job.id,
      title: job.customerReference || "Untitled Job",
      description: "",
      details: job.workDescription || "",
      due: job.date || "",
      assignedTo: job.assignedTo,
      isJobTask: true,
    });
  });

  if (!showAllDataFlag) {
    merged.columns = merged.columns.map(col => ({
      ...col,
      cards: col.cards.filter(c =>
        c.id === "sample-card" || (!c.isJobTask || c.assignedTo === userId)
      ),
    }));
  }

  const hasSampleCard = merged.columns.some(col =>
    col.cards.some(c => c.id === "sample-card")
  );

  if (!isSampleDeleted && !hasSampleCard) {
    merged.columns[0].cards.push({
      id: "sample-card",
      title: "This is a sample task",
      description: "Click on the arrow to see details...",
      details: "You can edit with the pencil, delete with the trash icon, then drag it to the next column when you start working on it.",
      assignedTo: userId,
    });
  }

  return merged;
}, [todos, projects, showAllDataFlag, userId]);


const visibleBoard = board;

  const addNewCard = async (newCard) => {

    if (newCard.id === "sample-card") {
      const hasSample = board.columns.some(col =>
        col.cards.some(c => c.id === "sample-card")
      );
      if (hasSample) return;
    }
    const newBoard = JSON.parse(JSON.stringify(board));
    const id = uuidv4();
    newCard.id = id;
    newCard.assignedTo = userId;

    newBoard.columns[0].cards.push(newCard);

    const todosRef = collection(db, "todos");
    const myDoc = getMyDoc();

    if (myDoc) {
      // update this user’s existing doc
      await updateDoc(doc(db, "todos", myDoc.id), {
        board: newBoard,
        userId,
        groupId,
      });
    } else {
      await addDoc(todosRef, {
        board: newBoard,
        userId,
        groupId,
        sampleDeleted: false,
      });
    }

  };

  const SortableCard = ({ card, children }) => {
    const isAssignedToCurrentUser = card.assignedTo === userId;

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: card.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 1 : "auto",
      touchAction: "manipulation",  // improve mobile responsiveness
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="task-card-wrapper"
      >
        <div
          {...attributes} {...listeners}
          onPointerDown={e => {
            const isInteractive =
              e.target.closest("button") ||
              e.target.closest("svg") ||
              e.target.closest("input") ||
              e.target.closest("textarea");
            if (isMobile && isInteractive) {
              e.stopPropagation();
            }
          }}
        >
          {children}
        </div>
      </div>
    );
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    setActiveCardId(null);

    if (!over || active.id === over.id) return;

    const sourceCol = visibleBoard.columns.find(col =>
      col.cards.some(card => card.id === active.id)
    );

    const destCol = visibleBoard.columns.find(col =>
      col.cards.some(card => card.id === over.id)
    ) || visibleBoard.columns.find(col => col.id === over.id);

    if (!sourceCol || !destCol) return;

    const movingCard = sourceCol.cards.find(card => card.id === active.id);
    if (!movingCard) return;

    const newBoard = { ...visibleBoard };

    if (sourceCol.id === destCol.id) {
      const column = newBoard.columns.find(col => col.id === sourceCol.id);
      const oldIndex = column.cards.findIndex(c => c.id === active.id);
      const newIndex = column.cards.findIndex(c => c.id === over.id);

      column.cards = arrayMove(column.cards, oldIndex, newIndex);
    } else {
      const sourceColumn = newBoard.columns.find(col => col.id === sourceCol.id);
      sourceColumn.cards = sourceColumn.cards.filter(card => card.id !== active.id);

      const destColumn = newBoard.columns.find(col => col.id === destCol.id);
      const overIndex = destColumn.cards.findIndex(card => card.id === over.id);
      const insertIndex = overIndex >= 0 ? overIndex : destColumn.cards.length;
      destColumn.cards.splice(insertIndex, 0, movingCard);
    }

    handleDataChange(newBoard);
  };

  const DroppableColumn = ({ columnId, children }) => {
    const { setNodeRef } = useDroppable({ id: columnId });
    return <div ref={setNodeRef}>{children}</div>;
  };


  const handleDataChange = async (newBoard) => {
    const oldFlat = visibleBoard.columns.flatMap(col =>
      col.cards.map(c => ({ ...c, from: col.id }))
    );
    const newFlat = newBoard.columns.flatMap(col =>
      col.cards.map(c => ({ ...c, to: col.id }))
    );

    const moved = newFlat.find(nc => {
      const oc = oldFlat.find(o => o.id === nc.id);
      return oc && oc.from !== nc.to;
    });
    if (!moved) return;

    const { id: cardId, to: destCol, isJobTask, assignedTo } = moved;

    const patchBoard = (baseBoard) => ({
      ...baseBoard,
      columns: baseBoard.columns.map(col => {
        const cards = col.cards.filter(c => c.id !== cardId);
        if (col.id === destCol) cards.push(moved);
        return { ...col, cards };
      }),
    });

    if (isJobTask) {


      try {
        await updateDoc(doc(db, "projects", cardId), {
          kanbanColumnId: destCol,
        });
      } catch (e) {
        console.warn("Could not update job column on project:", e);
      }
      const targetDoc = todos.find(d => d.userId === assignedTo);

      if (targetDoc) {
        const updated = patchBoard(targetDoc.board || defaultBoard);
        await updateDoc(doc(db, "todos", targetDoc.id), {
          board: updated,
          userId: assignedTo,
          groupId,
          sampleDeleted: targetDoc.sampleDeleted || false,
        });
      } else {
        await addDoc(collection(db, "todos"), {
          board: patchBoard(defaultBoard),
          userId: assignedTo,
          groupId,
          sampleDeleted: false,
        });
      }

      if (showAllDataFlag && assignedTo !== userId) {
        const myDoc = todos.find(d => d.userId === userId);
        if (myDoc) {
          const updated = patchBoard(myDoc.board || defaultBoard);
          await updateDoc(doc(db, "todos", myDoc.id), {
            board: updated,
            userId,
            groupId,
            sampleDeleted: myDoc.sampleDeleted || false,
          });
        }
      }
    } else {
      const myDoc = todos.find(d => d.userId === userId);
      if (!myDoc) return;
      const updated = patchBoard(myDoc.board || defaultBoard);
      await updateDoc(doc(db, "todos", myDoc.id), {
        board: updated,
        userId,
        groupId,
        sampleDeleted: myDoc.sampleDeleted || false,
      });
    }
  };


  const openEdit = (values) => {
    setEditOpen({ open: true, values });
  };


  const closeEdit = () => {
    setEditOpen({ open: false, values: {} });
  };

  const handleEdit = async (values) => {
    if (values.id && isJobTask(values.id)) return;

    setUpdating(true);
    closeEdit();

    const myDoc = getMyDoc();

    if (!myDoc) {
      const newBoard = {
        columns: defaultBoard.columns.map(col => {
          if (col.id !== 1) return { ...col, cards: [] };

          return {
            ...col,
            cards: [
              {
                id: values.id,
                title: values.title,
                description: values.description,
                details: values.details,
                due: values.due,
                assignedTo: values.assignedTo || userId,
              }
            ]
          };
        }),
      };

      await addDoc(collection(db, "todos"), {
        board: newBoard,
        userId,
        groupId,
        sampleDeleted: values.id === "sample-card",
      });

      setUpdating(false);
      return;
    }

    const currentBoard = myDoc.board || defaultBoard;

    const newBoard = {
      ...currentBoard,
      columns: currentBoard.columns.map((column) => ({
        ...column,
        cards: column.cards.map((card) =>
          card.id !== values.id
            ? card
            : {
              ...card,
              title: values.title,
              description: values.description,
              details: values.details,
              due: values.due,
              assignedTo: values.assignedTo || card.assignedTo,
            }
        ),
      })),
    };

    await updateDoc(doc(db, "todos", myDoc.id), {
      board: newBoard,
      userId,
      groupId,
      sampleDeleted: myDoc.sampleDeleted || false,
    });

    setUpdating(false);
  };




  const handleDelete = async (id) => {
    setUpdating(true);

    try {
      const myDoc = getMyDoc();
      if (!myDoc) return;

      // Remove the card from this user's board
      const newBoard = {
        ...myDoc.board,
        columns: myDoc.board.columns.map(column => ({
          ...column,
          cards: column.cards.filter(card => card.id !== id),
        })),
      };

      // Write back to this user's todos doc
      await updateDoc(doc(db, "todos", myDoc.id), {
        board: newBoard,
        userId,
        groupId,
        // If it’s the sample card being deleted, mark sampleDeleted
        sampleDeleted: id === "sample-card" ? true : myDoc.sampleDeleted || false,
      });
    } catch (error) {
      console.error("Error deleting task:", error);
    } finally {
      setUpdating(false);
    }
  };


  const myDoc = todos?.find(d => d.userId === userId);
  // fall back to a completely empty defaultBoard

  if (isMobile) {
    return (
      <Fade in={true}>
        <div>
          <Grid container>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragEnd={onDragEnd}
              onDragStart={(e) => setActiveCardId(e.active.id)}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography align="left" variant="h4">TASKS</Typography>
                </Grid>

                {visibleBoard.columns.map(column => (
                  <Grid item xs={12} key={column.id}>
                    <DroppableColumn columnId={column.id}>
                      <div className="task-column">
                        <Typography variant="h6" gutterBottom className="task-column-title">
                          {column.title}
                        </Typography>
                        <SortableContext
                          items={column.cards.map((card) => card.id)}
                          strategy={rectSortingStrategy}
                        >
                          {column.cards.map((card) => {
                            const isJob = isJobTask(card.id);
                            const isOther = card.assignedTo !== userId;
                            const profile = groupUsers.find(u => u.id === card.assignedTo);
                            const assignedToLabel = isJob && profile
                              ? `${profile.name} (${profile.email})`
                              : undefined;

                            return (
                              <SortableCard key={card.id} card={card}>
                                <Task
                                  title={card.title}
                                  description={card.description}
                                  isOtherUser={isOther}
                                  due={card.due}
                                  assignedTo={assignedToLabel}
                                  details={card.details}
                                  id={card.id}
                                  canEdit={(card.assignedTo === userId || card.id === "sample-card") && !isJob}
                                  openEdit={(card.assignedTo === userId || card.id === "sample-card") && !isJob ? openEdit : undefined}
                                  handleDelete={(card.assignedTo === userId || card.id === "sample-card") && !isJob ? handleDelete : undefined}
                                />
                              </SortableCard>
                            );
                          })}
                        </SortableContext>
                      </div>
                    </DroppableColumn>
                  </Grid>
                ))}
              </Grid>

              <DragOverlay>
                {activeCardId && (
                  <Task
                    {...board.columns
                      .flatMap(col => col.cards)
                      .find(c => c.id === activeCardId)}
                    canEdit={false}
                  />
                )}
              </DragOverlay>
            </DndContext>
          </Grid>

          <NewToDo
            contacts={contacts}
            addNewCard={addNewCard}
            isInactive={props.isInactive}
          />
          <EditToDo
            contacts={contacts}
            open={editOpen.open}
            closeEdit={closeEdit}
            handleEdit={handleEdit}
            addNewCard={addNewCard}
            values={editOpen.values}
          />
        </div>
      </Fade>
    );
  }



  return (
    <>
      <Fade in={true}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={8} style={{ position: "sticky" }}>
            <Typography align="left" variant="h4">
              TASKS
            </Typography>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Board
              key={JSON.stringify(visibleBoard)}
              initialBoard={visibleBoard}
              renderCard={(
                { title, description, due, details, assignedTo: rawAssignedTo, id }, { dragging },
              ) => {
                const isJob = isJobTask(id);
                const isOther = rawAssignedTo !== userId;
                const profile = groupUsers.find(u => u.id === rawAssignedTo);
                const assignedToLabel = isJob && profile
                  ? `${profile.name} (${profile.email})`
                  : undefined;

                const canEdit = (rawAssignedTo === userId || id === "sample-card") && !isJobTask(id);
                return (
                  <Task
                    dragging={dragging}
                    isOtherUser={isOther}
                    title={title}
                    description={description}
                    due={due}
                    assignedTo={assignedToLabel}
                    details={details}
                    id={id}
                    canEdit={canEdit}
                    openEdit={canEdit ? openEdit : undefined}
                    handleDelete={canEdit ? handleDelete : undefined}
                  />
                );
              }}
              onCardDragEnd={handleDataChange}
              onColumnDragEnd={handleDataChange}
            />
          </Grid>
        </Grid>
      </Fade>
      <NewToDo
        contacts={contacts}
        addNewCard={addNewCard}
        isInactive={props.isInactive}
      />
      <EditToDo
        contacts={contacts}
        open={editOpen.open}
        closeEdit={closeEdit}
        handleEdit={handleEdit}
        addNewCard={addNewCard}
        values={editOpen.values}
      />
    </>
  );
};

export default ToDo;